import { apiClient } from '@/lib/api-client';
import type { components } from '@/lib/api-types-identity-documents';
import { createPdfEngine, type PdfEngine } from '@/tools/pdf-engine';

export type ReceiptPrefill = components['schemas']['ReceiptPrefillResponse'];

export type PrefillOutcome =
  | { readonly kind: 'ok'; readonly prefill: ReceiptPrefill }
  /** The model or an access check is down (503): the user simply fills the fields in. */
  | { readonly kind: 'unavailable' }
  | { readonly kind: 'failed' };

/** The server reads at most a receipt's first five pages (#497, spec open point 2). */
export const PREFILL_MAX_PAGES = 5;

type Media = 'application/pdf' | 'image/jpeg' | 'image/png';

/**
 * «Note spese» prefill (decision 130): sends the expense's document to the identity-documents
 * service, which reads it with the EU-hosted Gemini model and stores nothing. A PDF longer than five
 * pages is cut to its first five in the browser first. HEIC is converted to JPEG before this, by the
 * capture screen. Every proposed field stays editable; nothing here saves the expense.
 */
export async function prefillReceipt(file: Blob, engine?: PdfEngine): Promise<PrefillOutcome> {
  const mediaType = file.type as Media;
  if (!['application/pdf', 'image/jpeg', 'image/png'].includes(mediaType))
    return { kind: 'failed' };
  const pdfEngine = mediaType === 'application/pdf' ? (engine ?? createPdfEngine()) : undefined;
  try {
    const sent = pdfEngine === undefined ? file : await firstPages(file, pdfEngine);
    const { data, response } = await apiClient.POST('/v1/identity-documents/receipts/prefill', {
      body: { document: { mediaType, base64Data: await toBase64(sent) } },
    });
    if (response.status === 503) return { kind: 'unavailable' };
    if (!response.ok || data === undefined) return { kind: 'failed' };
    return { kind: 'ok', prefill: data };
  } catch {
    return { kind: 'failed' };
  } finally {
    if (engine === undefined) pdfEngine?.dispose();
  }
}

async function firstPages(file: Blob, engine: PdfEngine): Promise<Blob> {
  const opened = await engine.open(file);
  try {
    if (opened.info.pages <= PREFILL_MAX_PAGES) return file;
    const [cut] = await engine.split(opened.id, [[0, PREFILL_MAX_PAGES - 1]]);
    return cut ?? file;
  } finally {
    engine.close(opened.id);
  }
}

async function toBase64(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = '';
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  }
  return btoa(binary);
}
